import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Wallet as WalletIcon, Users, Plus, Lock, Sparkles, ShieldCheck, FileText, RotateCcw, Headphones, ExternalLink, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UpgradeDialog from "@/components/UpgradeDialog";
import { format } from "date-fns";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [busyP, setBusyP] = useState(false);
  const [busyPw, setBusyPw] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();

  const isPro = user?.plan === "pro" || user?.role === "superadmin";

  const saveProfile = async () => {
    setBusyP(true);
    try {
      const { data } = await api.patch("/auth/profile", { name, mobile });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyP(false);
    }
  };

  const changePassword = async () => {
    setBusyPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password changed");
      setCurrentPw("");
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPw(false);
    }
  };

  return (
    <div className="space-y-5 lg:max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">You</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Profile</h1>
      </div>

      <div className="rounded-3xl p-6 bg-white border border-slate-200 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden ring-1 ring-slate-200 bg-slate-100 grid place-items-center shrink-0">
          {user?.photo ? (
            <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-8 h-8 text-slate-400" />
          )}
        </div>
        <div className="flex-1">
          <div className="heading text-lg font-bold text-slate-900">{user?.name}</div>
          <div className="text-sm text-slate-500">{user?.email}</div>
        </div>
        <div className="text-right">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
            isPro ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
          }`}>{user?.role === "superadmin" ? "SUPER ADMIN" : (user?.plan || "free")}</span>
          {user?.plan === "pro" && user?.plan_expires_at && (
            <div className="text-[10px] text-slate-500 mt-1">Renews {format(new Date(user.plan_expires_at), "PP")}</div>
          )}
        </div>
      </div>

      {!isPro && (
        <div className="rounded-3xl p-5 bg-slate-900 text-white flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500/20 grid place-items-center">
            <Sparkles className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="flex-1">
            <div className="heading font-semibold">Go Pro — unlimited groups</div>
            <div className="text-sm text-slate-300 mt-0.5">Create as many groups as you need. One-time upgrade.</div>
          </div>
          <button onClick={() => setUpgradeOpen(true)} className="h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold active:scale-95" data-testid="profile-upgrade-button">
            Upgrade
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-3xl p-5 bg-white border border-slate-200 space-y-4">
          <div className="text-sm font-semibold text-slate-900">Basic info</div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="profile-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Mobile</Label>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="profile-mobile-input" />
          </div>
          <Button disabled={busyP} onClick={saveProfile} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="profile-save-button">
            {busyP ? "Saving…" : "Save changes"}
          </Button>
        </section>

        <section className="rounded-3xl p-5 bg-white border border-slate-200 space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-600" />
            <div className="text-sm font-semibold text-slate-900">Password</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Current</Label>
            <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="mt-2 h-12 rounded-2xl" data-testid="profile-currentpw" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">New (min 6)</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={6} className="mt-2 h-12 rounded-2xl" data-testid="profile-newpw" />
          </div>
          <Button disabled={busyPw || !currentPw || !newPw} onClick={changePassword} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="profile-changepw-btn">
            {busyPw ? "Updating…" : "Update password"}
          </Button>
        </section>
      </div>

      <div className="rounded-3xl p-2 bg-white border border-slate-200 divide-y divide-slate-100">
        <div className="p-1">
          <MenuItem icon={WalletIcon} label="My Wallet" onClick={() => navigate("/wallet")} testid="menu-wallet" />
          <MenuItem icon={Users} label="My groups" onClick={() => navigate("/switch")} testid="menu-groups" />
          <MenuItem icon={Plus} label="Create a new group" onClick={() => navigate("/new")} testid="menu-create-group" />
          <MenuItem icon={Sparkles} label="Subscription" onClick={() => navigate("/subscription")} testid="menu-subscription" />
          {user?.role === "superadmin" && (
            <MenuItem icon={ShieldCheck} label="Super admin panel" onClick={() => navigate("/admin")} testid="menu-admin" />
          )}
        </div>
        <div className="p-1">
          <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">Legal & Support</div>
          <MenuItem icon={ShieldCheck} label="Privacy Policy" onClick={() => navigate("/privacy-policy")} testid="menu-privacy" />
          <MenuItem icon={FileText} label="Terms & Conditions" onClick={() => navigate("/terms")} testid="menu-terms" />
          <MenuItem icon={RotateCcw} label="Refund Policy" onClick={() => navigate("/refund-policy")} testid="menu-refund" />
          <a
            href="https://saharey.com/contactus"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-between p-4 rounded-2xl hover:bg-slate-50 transition-colors text-slate-800"
            data-testid="menu-contact"
          >
            <div className="flex items-center gap-3">
              <Headphones className="w-4 h-4" />
              <span className="text-sm font-semibold">Contact Us</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
          </a>
        </div>
        <div className="p-1">
          <MenuItem icon={LogOut} label="Sign out" onClick={logout} danger testid="menu-logout" />
        </div>
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} onUpgraded={() => window.location.reload()} />
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 p-4 rounded-2xl hover:bg-slate-50 transition-colors ${danger ? "text-rose-600" : "text-slate-800"}`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );
}
